<template>
  <div
    v-on-clickaway="hideDropdown"
    :class="classes"
  >
    <!-- FIELD -->
    <div
      class="datepicker__field-wrapper"
      @click="toggleDropdownVisibility"
    >
      <label v-if="label" class="datepicker__label">
        {{ label }}
        <span v-if="required" class="datepicker__label-required">*</span>
      </label>

      <div class="datepicker__field">
        <div class="datepicker__text">
          <span class="datepicker__date">{{ formatDate(date) }}</span>
          <span class="datepicker__time">{{ time }}</span>
        </div>
        <IconCalendar class="datepicker__calendar" />
      </div>
    </div>
    <!-- FIELD -->

    <!-- DROPDOWN -->
    <transition name="slide-from-top-short">
      <div
        v-if="isDropdownVisible"
        class="datepicker__dropdown"
      >
        <!-- CALENDAR -->
        <transition :name="dropdownInsideTransitionName" mode="out-in">
          <div
            v-if="!isPickingTime"
            key="date-picker"
            class="datepicker__dropdown-date"
          >
            <datepicker
              :value="date"
              v-bind="datepickerOptions"
              @input="onDateChange"
            />
            <div class="datepicker__dropdown-button-container">
              <div
                class="datepicker__dropdown-button"
                @click="onPickTime"
              >
                {{ buttonsText.pickTime }}
              </div>
            </div>
          </div>
          <!-- CALENDAR -->

          <!-- TIME -->
          <div
            v-else
            key="time-picker"
            class="datepicker__dropdown-time"
          >
            <div class="datepicker__dropdown-time-title">{{ formatDate(date) }}</div>
            <div class="datepicker__dropdown-times">
              <div
                v-for="(timeItem, index) in getAvailableTimes(date)"
                :key="`time-item-${index}`"
                :class="[
                  'datepicker__dropdown-time-item',
                  { 'datepicker__dropdown-time-item_disabled': timeItem.disabled },
                  { 'datepicker__dropdown-time-item_selected': isTimeSelected(timeItem) }
                ]"
                @click="selectTime(timeItem)"
              >
                {{ timeItem.value }}
              </div>
            </div>
            <div class="datepicker__dropdown-button-container">
              <div
                class="datepicker__dropdown-back-to-calendar-button"
                @click="backToDate"
              >
                <IconChevron class="datepicker__dropdown-back-to-calendar-button-arrow" />
                <div class="datepicker__dropdown-back-to-calendar-button-text">{{ buttonsText.backToCalendar }}</div>
              </div>
            </div>
            <div class="datepicker__dropdown-button-container">
              <div
                class="datepicker__dropdown-button"
                @click="hideDropdown"
              >
                {{ buttonsText.submit }}
              </div>
            </div>
          </div>
          <!-- TIME -->
        </transition>
      </div>
    </transition>
    <!-- DROPDOWN -->

    <transition name="slide-from-top">
      <span
        v-if="error && errorText"
        class="datepicker__error-msg"
      >
        {{ errorText }}
      </span>
    </transition>
  </div>
</template>

<script>
  import { format, getDay, differenceInMinutes } from 'date-fns'
  import { uk, ro, ru } from 'date-fns/locale'
  import { uk as datapickerUk } from 'vuejs-datepicker/dist/locale'
  import { ru as datapickerRu } from 'vuejs-datepicker/dist/locale'
  import { ro as datapickerRo } from 'vuejs-datepicker/dist/locale'
  import Datepicker from 'vuejs-datepicker'
  import IconChevron from '@/components/svg/IconChevron'
  import IconCalendar from "@/components/svg/IconCalendar"
  import { mixin as clickaway } from 'vue-clickaway'
  import { cloneDeep, get } from 'lodash'

  export default {
    name: 'BaseDatePicker',

    components: {
      Datepicker,
      IconChevron,
      IconCalendar
    },

    mixins: [ clickaway ],

    props: {
      label: {
        type: String,
        default: ''
      },
      errorText: {
        type: String,
        default: ''
      },
      placeholder: {
        type: String,
        default: ''
      },
      lang: {
        type: String,
        default: 'uk'
      },
      required: {
        type: Boolean,
        default: false
      },
      buttonsText: {
        type: Object,
        required: true,
        validator (buttonsText) {
          return 'pickTime' in buttonsText &&
            'submit' in buttonsText &&
            'backToCalendar' in buttonsText
        }
      },
      options: {
        type: [Object],
        required: true,
        default: () => ({})
      }
    },

    data () {
      return {
        date: null,
        time: '',
        isPickingTime: false,
        datesWithNoAvailableTime: [],
        isDropdownVisible: false,
        pickTime: false,
        error: null
      }
    },

    computed: {
      classes () {
        return [
          'datepicker',
          {'datepicker_active': this.isDropdownVisible},
          {'datepicker_error': this.error},
          {'datepicker_IE': this.isIeBrowser}
        ]
      },

      isIeBrowser () {
        return window.navigator.userAgent.indexOf('MSIE ') > -1 || window.navigator.userAgent.indexOf('Trident/') > -1
      },

      datepickerOptions () {
        return {
          'monday-first': true,
          'full-month-name': true,
          'maximum-view': 'day',
          'use-utc': true,
          'inline': true,
          'language': this.getCurrentCalendarLang(),
          'format': this.formatDate(),
          'disabledDates': {
            to: new Date(new Date().setDate(new Date().getDate() - 1)), // yesterday
            days: this.disabledWeekDays,
            dates: [...this.datesWithNoAvailableTime, new Date()]
          }
        }
      },
      dropdownInsideTransitionName () {
        return this.isPickingTime
          ? 'slide-to-left'
          : 'slide-to-right'
      },
      dateTime () {
        // resulting timestamp (emiting value from component)
        const date = cloneDeep(this.date)
        const hours = Number(this.time.split(':')[0])
        const minutues = Number(this.time.split(':')[1])
        date.setHours(hours, minutues, 0, 0)
        return date.getTime()
      },
      disabledWeekDays () {
        // format to US day style
        // from 0 = Monday, 6 = Sunday
        // to 0 = Sunday, 6 = Saturday
        return this.options.schedule.disabled.map(dayIndex => {
          return dayIndex === 6
            ? 0
            : dayIndex + 1
        })
      }
    },

    watch: {
      errorText: {
        handler (errorText) {
          this.error = !!this.errorText
        },
        immediate: true
      }
    },

    methods: {
      getCurrentCalendarLang () {
        if(this.lang === 'ro') {
          return datapickerRo
        } else if(this.lang === 'ru') {
          return datapickerRu
        } else {
          return datapickerUk
        }
      },
      initDatePicker () {
        // select first available date and time
        this.onDateChange(this.getFirstAvailableDate())
      },
      getFirstAvailableDate () {
        const today = new Date()
        const tomorrow = new Date()
        tomorrow.setDate(today.getDate() + 1)

        let availableDate = tomorrow

        while (!this.getFirstAvailableTime(availableDate)) {
          const thisDay = cloneDeep(availableDate)
          const nextDay = cloneDeep(thisDay)
          nextDay.setDate(thisDay.getDate() + 1)

          // disable day on calendar if it has no available time
          this.datesWithNoAvailableTime = [...this.datesWithNoAvailableTime, cloneDeep(thisDay)]

          availableDate = cloneDeep(nextDay)
        }

        return availableDate
      },
      getFirstAvailableTime (date) {
        const availableTime = this.getAvailableTimes(date).find(time => {
          return !('disabled' in time) || !time.disabled
        })

        return get(availableTime, 'value')
      },
      getAvailableTimes (date) {
        const selectedWeekDayIndex = getDay(date) === 0
          ? 6
          : getDay(date) - 1

        const isToday = () => {
          return date.getFullYear() === new Date().getFullYear() &&
            date.getMonth() === new Date().getMonth() &&
            date.getDate() === new Date().getDate()
        }

        if (isToday()) {
          // disable already passed times
          return this.options.schedule.enabled[selectedWeekDayIndex]
            ? this.options.schedule.enabled[selectedWeekDayIndex]
              .map(time => {
                const timeToCompare = new Date()
                const hours = Number(time.value.split(':')[0])
                const minutues = Number(time.value.split(':')[1])
                timeToCompare.setHours(hours, minutues, 0, 0)

                const isTimePass = differenceInMinutes(timeToCompare, new Date()) < 0
                return isTimePass
                  ? { ...time, disabled: true }
                  : time
              })
            : []
        }

        return this.options.schedule.enabled[selectedWeekDayIndex] || []
      },

      currentLocalLang () {
        if(this.lang === 'ro') {
          return ro
        } else if(this.lang === 'ru') {
          return ru
        } else {
          return uk
        }
      },

      formatTime (date) {
        return format(date, 'HH:mm', { locale: this.currentLocalLang() })
      },
      formatDate (date) {
        if (!date) return this.placeholder || ''
        return format(date, 'd MMMM yyyy', { locale: this.currentLocalLang() })
      },

      onDateChange (date) {
        this.date = date
        this.$emit('input', this.dateTime)
        this.onTimeChange(this.getFirstAvailableTime(date))
      },
      onTimeChange (time) {
        this.time = time
        this.$emit('input', this.dateTime/1000)
      },
      backToDate () {
        this.isPickingTime = false
        setTimeout(()=>{
          this.createAndSetCustomPrevArrow()
          this.createAndSetCustomNextArrow()
        },700)
      },
      onPickTime () {
        this.isPickingTime = true
      },
      isTimeSelected (time) {
        return this.time === time.value
      },
      selectTime (time) {
        if (time.disabled) return
        this.onTimeChange(time.value)
      },

      toggleDropdownVisibility () {
        this.isDropdownVisible
          ? this.hideDropdown()
          : this.showDropdown()
      },
      showDropdown () {
        this.isPickingTime = false
        this.isDropdownVisible = true
        setTimeout(()=> {
          // const defaultEl = this.$el.querySelector('.day__month_btn')
          // const elContent = defaultEl.innerHTML
          // const month = elContent.slice(0,elContent.length - 4)
          // const year = elContent.slice(elContent.length - 4,elContent.length)
          // const yearEl = document.createElement('span')
          // defaultEl.innerHTML = month
          // defaultEl.appendChild(yearEl)
          // yearEl.innerHTML = year
          this.createAndSetCustomPrevArrow ()
          this.createAndSetCustomNextArrow ()
        }, 5)
      },
      createAndSetCustomPrevArrow () {
        const prevButton = this.$el.querySelector('.prev')
        const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg")
        const svgG = document.createElementNS("http://www.w3.org/2000/svg", "g");
        const svgGPath = document.createElementNS("http://www.w3.org/2000/svg", "path");

        svg.setAttribute ("width", "10" )
        svg.setAttribute ("height", "8" )
        svg.setAttribute('viewBox','0 0 10 6')

        svgG.setAttribute('data-name','Layer 2')

        svgGPath.setAttribute('fill','none')
        svgGPath.setAttribute('stroke','#989a9d')
        svgGPath.setAttribute('stroke-linecap','round')
        svgGPath.setAttribute('stroke-linejoin','round')
        svgGPath.setAttribute('stroke-width','2')
        svgGPath.setAttribute('d','M9 1L5 5 3.04 3.04 1 1')
        svgGPath.setAttribute('data-name','Layer 1')

        prevButton.appendChild(svg)
        svg.appendChild(svgG)
        svgG.appendChild(svgGPath)
      },
      createAndSetCustomNextArrow () {
        const nextButton = this.$el.querySelector('.next')
        const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg")
        const svgG = document.createElementNS("http://www.w3.org/2000/svg", "g");
        const svgGPath = document.createElementNS("http://www.w3.org/2000/svg", "path");

        svg.setAttribute ("width", "10" )
        svg.setAttribute ("height", "8" )
        svg.setAttribute('viewBox','0 0 10 6')

        svgG.setAttribute('data-name','Layer 2')

        svgGPath.setAttribute('fill','none')
        svgGPath.setAttribute('stroke','#989a9d')
        svgGPath.setAttribute('stroke-linecap','round')
        svgGPath.setAttribute('stroke-linejoin','round')
        svgGPath.setAttribute('stroke-width','2')
        svgGPath.setAttribute('d','M9 1L5 5 3.04 3.04 1 1')
        svgGPath.setAttribute('data-name','Layer 1')


        nextButton.appendChild(svg)
        svg.appendChild(svgG)
        svgG.appendChild(svgGPath)
      },
      hideDropdown () {
        this.isDropdownVisible = false
        this.isPickingTime = false
      }
    }
  }
</script>

<style lang="sass">
  @import '@/assets/sass/partials/_sass-components.sass'

  $padding-for-error: 28px

  .datepicker 
    position: relative
    display: block
    min-width: 290px
    margin-bottom: 24px
    cursor: pointer
    +res('mobXs')
      min-width: 280px

    &.disabled
      pointer-events: none
      opacity: 0.6
      .datepicker__field-wrapper 
        pointer-events: none
    
    &_IE
      .day-header,
      .day
        width: calc( 100% / 7.2) !important

    &__label
      display: flex
      font-family: $text-font-book
      color: $inputLabel
      padding-bottom: 10px
      font-size: 16px

      &-required
        color: $red
        margin-left: 5px

    &__field 
      position: relative
      display: block
      width: 100%
      min-height: 48px
      padding: 0.938em 1.3em
      font-size: 16px
      line-height: 1
      text-align: left
      background-color: $grey
      box-shadow: inset 0 1px 1px rgba(0,0,0,0.075)
      border-radius: 4px
      outline: none
      caret-color: red
      color: $black
      transition: 0.3s
      +res('desk')
        &:hover
          background-color: $form-hover

    &__calendar
      position: absolute
      right: 10px
      bottom: 10px
      width: 26px
      height: 26px

    &__date 
      margin-right: 50px
      font-family: $text-font-book
      color: $black
      +res('mob')
        margin-right: 25px

    &__time
      font-family: $text-font-book
      color: $black

    &__dropdown 
      position: absolute
      top: calc(100% + 3px)
      right: 0
      z-index: 9999
      width: 100%
      max-width: 370px
      padding: 15px 0 0
      font-family: $text-font-book
      cursor: default
      user-select: none
      background-color: $white
      box-shadow: 0 2px 10px 0 rgba(194, 192, 192, 0.5)
      border-radius: 4px
      +res('mob') 
        padding: 5px 0
        max-width: 322px
      +res('mobXs')
        max-width: 280px

      .vdp-datepicker__calendar 
        width: 100%
        margin: 0 auto
        font-family: $text-font-book
        background: white
        border: none
        +res('mob')
          padding-left: 0

        header 
          display: flex
          justify-content: space-between
          padding: 0
          font-size: 24px
          font-weight: 700
          span
            float: inherit !important
          span > span
            font-size: 17px
            color: #989a9d 
            font-weight: 400
            width: 45px !important

        .prev,
        .next 
          position: relative
          overflow: hidden
          &:hover
            background-color: transparent !important

          &::after 
            display: none

          svg 
            position: absolute
            z-index: 10
        
        .prev
          transform: rotate(-270deg)
          svg
            top: 16px
            left: 21px

        .next
          transform: rotate(270deg)
          svg
            top: 10px
            left: 22px
            +res('mob')
              left: 15px
            +res('mobXs')
              left: 11px

        .cell 
          border: none !important
          transition: all 0.3s
          &:hover

          &.day
            position: relative
            z-index: 1
            width: calc(100% / 7)
            height: 52px
            padding: 5px
            color: $inputLabel
            border-radius: 50%
            +res('desk')
              &:hover
                &:after
                  opacity: 1
            +res('mob')
              width: 46px
              width: 46px
            +res('mobXs')
              width: 40px
              height: 45px
            &:after
              position: absolute
              top: 7px
              left: 7px
              z-index: -1
              content: ''
              width: 38px
              height: 38px
              border-radius: 50%
              background-color: $grey
              opacity: 0
              +res('tab')
                top: 7px
                left: 7px
              +res('mob')
                top: 7px
                left: 4px
              +res('mobXs')
                top: 7px
                left: 2px
            &.selected
              position: relative
              z-index: 1
              color: $white !important
              background: $white !important
              &:hover
                background-color: transparent !important
              &:after
                opacity: 1
                background: $darkRed
                +res('mobXs')
                  top: 7px
                  left: 1px
            &.blank
              &:after 
                background-color: white !important

          &.weekend 
            position: relative
            z-index: 1
            background-color: $grey
            border-radius: 0
            +res('desk')
              &:hover
                &:after
                  opacity: 1
            &:after
              position: absolute
              top: 5px
              left: 5px
              z-index: -1
              content: ''
              width: 38px
              height: 38px
              border-radius: 50%
              background-color: $white
              opacity: 0
            &.selected
              background-color: $grey !important
              &:hover
                background-color: $grey !important

          &.day-header
            color: $inputLabel
            text-transform: uppercase
            width: calc(100% / 7)
            height: 52px
            border-bottom: 1px solid $form-hover !important
            &:nth-child(6),
            &:nth-child(7)
              background-color: $grey
            +res('mob')
              font-size: 10px
              width: 46px
              height: 46px
            +res('mobXs')
              width: 40px
              height: 40px

          &.blank 
            color: $black

          &.disabled 
            color: $disableDay
            pointer-events: none

      &-button 
        height: 48px
        border-radius: 24px
        background-color: $grey
        color: $black
        font-family: $text-font-book
        padding: 0 33px
        display: flex
        align-items: center
        cursor: pointer
        font-size: 15px
        transition: background .3s
        +res('desk')
          &:hover
            background: $form-hover

        &-container 
          display: flex
          justify-content: center
          margin: 20px 0

      &-time 
        padding-right: 10px
        padding-left: 10px

        &-title 
          margin: 10px 0 25px
          text-align: center

      &-times 
        display: flex
        flex-wrap: wrap
        margin-top: 10px

      &-time-item 
        flex: 0 1 20%
        margin-bottom: 25px
        color: $inputLabel
        text-align: center
        cursor: pointer
        transition: color 0.3s
        +res('desk')
          &:hover 
            color: $darkRed

        &_disabled 
          color: $disableDay
          pointer-events: none
        

        &_selected 
          color: $darkRed

      &-back-to-calendar-button 
        display: flex
        align-items: center
        margin-right: 5px
        cursor: pointer

        &-arrow 
          width: 16px
          height: 16px
          margin-right: 10px
          transition: 0.3s
        

        &-text 
          font-size: 14px
          line-height: 16px
          transition: color 0.3s
        

        &:hover 
          .datepicker__dropdown-back-to-calendar-button 
            &-text 
              color: $darkRed
            &-arrow 
              stroke: $darkRed

    &__error-msg 
      position: absolute
      bottom: -5px
      left: 0
      z-index: 1
      height: 0
      font-size: 12px
      color: $red
    

    &_error 
      .datepicker
        +res('tab')
          margin-bottom: 35px
        +res('mobXs')
          margin-bottom: 35px !important

    &_active 
      .datepicker 
        &__field 
          background-color: $white
          box-shadow: 0 2px 10px 0 rgba(194, 192, 192, 0.5)

</style>
